import { errorMessage } from "helpers/utils";
import { useMutation, useQuery } from "react-query";
import ticketService from "services/ticketService";

function unwrapAxiosBody(raw) {
  if (raw == null) {
    return null;
  }

  if (raw.data !== undefined && typeof raw.status === "number") {
    return raw.data;
  }

  return raw;
}

function pickTicketListFromResponse(raw) {
  const body = unwrapAxiosBody(raw) ?? {};

  return {
    items: Array.isArray(body.items) ? body.items : [],
    totalCount: body._meta?.totalCount ?? 0,
  };
}

function pickTicketHistoryItemsFromResponse(raw) {
  const body = unwrapAxiosBody(raw) ?? {};
  const items = body.items;

  return Array.isArray(items) ? items : [];
}

function notifyQueryError(onError, error) {
  const errorResponse = errorMessage(error);

  if (typeof onError === "function") {
    onError(errorResponse);
  }
}

function createMutationOptions(onSuccess, onError) {
  return {
    retry: 0,
    onSuccess,
    onError: error => notifyQueryError(onError, error),
  };
}

export const useTicketList = (payload, onSuccess, onError) => {
  const params = payload?.params || {};

  const queryOptions = {
    enabled: false,
    retry: 1,
    keepPreviousData: true,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => notifyQueryError(onError, error),
    select: response => pickTicketListFromResponse(response),
  };

  return useQuery(
    ["ticket_list", params],
    () => ticketService.listTickets({ params }),
    queryOptions
  );
};

export const useCreateTicket = (onSuccess, onError) => {
  return useMutation(
    payload => ticketService.createTicket(payload),
    createMutationOptions(onSuccess, onError)
  );
};

export const useTicketDetail = ticketId => {
  return useQuery(
    ["ticket_detail", ticketId],
    () => ticketService.detailTicket(ticketId),
    {
      enabled: Boolean(ticketId),
      retry: 1,
      select: response => unwrapAxiosBody(response),
    }
  );
};

export const useTicketHistory = ticketId => {
  return useQuery(
    ["ticket_history", ticketId],
    () => ticketService.ticketHistory(ticketId),
    {
      enabled: Boolean(ticketId),
      retry: 1,
      select: response => pickTicketHistoryItemsFromResponse(response),
    }
  );
};

export const useSkipTicket = (onSuccess, onError) => {
  return useMutation(ticketService.skipTicket, createMutationOptions(onSuccess, onError));
};

export const useCancelTicket = (onSuccess, onError) => {
  return useMutation(ticketService.cancelTicket, createMutationOptions(onSuccess, onError));
};

export const useLockTicket = (onSuccess, onError) => {
  return useMutation(ticketService.lockTicket, createMutationOptions(onSuccess, onError));
};

export const useSendToSale = (onSuccess, onError) => {
  return useMutation(ticketService.sendToSale, createMutationOptions(onSuccess, onError));
};

export const useTicketStatus = (onSuccess, onError) => {
  return useQuery(
    ["ticket-status"],
    () => ticketService.listTicketStatus(),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => notifyQueryError(onError, error),
      select: response => {
        const payload = unwrapAxiosBody(response);

        if (!payload || typeof payload !== "object") {
          return [];
        }

        return Object.keys(payload).map(key => ({
          value: key,
          label: payload[key],
        }));
      },
    }
  );
};
